import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocFromServer,
  updateDoc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged
} from 'firebase/auth';
import { firebaseConfig } from './firebaseConfig';

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

const convertTimestamp = (value) => value?.toDate?.() || value || null;
const statusReference = doc(db, 'status', 'plex');

const emptyStatus = (error) => ({
  lastHeartbeat: null,
  adminMessage: '',
  activeStreamCount: null,
  activityUpdatedAt: null,
  ...(error ? { error } : {})
});

const mapStatusSnapshot = (snapshot) => {
  if (!snapshot.exists()) return emptyStatus();

  const data = snapshot.data();
  return {
    lastHeartbeat: convertTimestamp(data.lastHeartbeat),
    adminMessage: data.adminMessage || '',
    messageUpdatedAt: convertTimestamp(data.messageUpdatedAt),
    activeStreamCount: Number.isInteger(data.activeStreamCount) ? data.activeStreamCount : null,
    activityUpdatedAt: convertTimestamp(data.activityUpdatedAt),
    uptimeTrackingStartedAt: convertTimestamp(data.uptimeTrackingStartedAt),
    uptimeDowntime24Hours: data.uptimeDowntime24Hours,
    uptimeDowntime7Days: data.uptimeDowntime7Days,
    uptimeDowntime30Days: data.uptimeDowntime30Days,
    uptimeTotalDowntimeSeconds: data.uptimeTotalDowntimeSeconds
  };
};

const testViewerPreviews = {
  'test-preview': ['Andrew', 'Sarah'],
  'test-preview-2': ['Michael'],
  'test-preview-3': [],
  'test-preview-multiday': ['Andrew', 'Emma', 'James']
};

// The second callback argument tells pages whether this result is cached or
// confirmed by the Firestore server. Existing callers can safely ignore it.
export const getStatusListener = (callback) => onSnapshot(
  statusReference,
  { includeMetadataChanges: true },
  (snapshot) => callback(mapStatusSnapshot(snapshot), { fromCache: snapshot.metadata.fromCache }),
  (error) => {
    console.error('Error listening to status:', error);
    callback(emptyStatus(error.message), { fromCache: true, error });
  }
);

// Unlike a normal listener's first result, this never returns Firestore's
// local cache. It is used by the public status page before showing live data.
export const getStatusFromServer = async () => mapStatusSnapshot(
  await getDocFromServer(statusReference)
);

export const getPrivateActivityListener = (callback) => onSnapshot(
  doc(db, 'privateActivity', 'latest'),
  (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    const data = snapshot.data();
    callback({
      capturedAt: convertTimestamp(data.capturedAt),
      streamCount: data.streamCount || 0,
      sessions: Array.isArray(data.sessions) ? data.sessions : []
    });
  },
  (error) => {
    console.error('Error listening to private activity:', error);
    callback(null, error);
  }
);

export const getOutageHistoryListener = (callback) => onSnapshot(
  query(collection(db, 'outageHistory'), orderBy('startedAt', 'desc')),
  (snapshot) => callback(snapshot.docs.map((outageDocument) => {
    const data = outageDocument.data();
    return {
      id: outageDocument.id,
      startedAt: convertTimestamp(data.startedAt),
      endedAt: convertTimestamp(data.endedAt),
      durationSeconds: Number(data.durationSeconds) || 0,
      viewersAtStart: Array.isArray(data.viewersAtStart)
        ? data.viewersAtStart
        : (testViewerPreviews[outageDocument.id] ?? null)
    };
  })),
  (error) => {
    console.error('Error listening to outage history:', error);
    callback([], error);
  }
);

export const getAdminMessageHistoryListener = (callback) => onSnapshot(
  query(collection(db, 'adminMessageHistory'), orderBy('postedAt', 'desc')),
  (snapshot) => callback(snapshot.docs.map((messageDocument) => {
    const data = messageDocument.data();
    return {
      id: messageDocument.id,
      message: data.message || '',
      postedAt: convertTimestamp(data.postedAt),
      autoExpireAfterRecovery: data.autoExpireAfterRecovery === true
    };
  })),
  (error) => {
    console.error('Error listening to status-message history:', error);
    callback([], error);
  }
);

export const updateAdminMessage = async (message, autoExpireAfterRecovery = false) => {
  const batch = writeBatch(db);
  const statusRef = statusReference;
  const historyRef = doc(collection(db, 'adminMessageHistory'));
  const postedAt = serverTimestamp();

  batch.update(statusRef, {
    adminMessage: message,
    messageUpdatedAt: postedAt,
    adminMessageAutoExpire: autoExpireAfterRecovery,
    adminMessageExpiryArmed: false,
    adminMessageSuccessfulHeartbeats: 0
  });
  batch.set(historyRef, { message, postedAt, autoExpireAfterRecovery });
  await batch.commit();
};

export const clearAdminMessage = () => updateDoc(statusReference, {
  adminMessage: '',
  messageUpdatedAt: serverTimestamp(),
  adminMessageAutoExpire: false,
  adminMessageExpiryArmed: false,
  adminMessageSuccessfulHeartbeats: 0
});

export const signInWithGoogle = async () => {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
};

export const signOut = () => firebaseSignOut(auth);
export const onAuthStateChanged = (callback) => firebaseOnAuthStateChanged(auth, callback);
